import { useId } from 'react'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

/**
 * The artwork on the login screen's brand panel.
 *
 * A QR sticker being scanned, and behind it a rotating cast of the things that
 * carry one: a cable drum, a machine on the floor, a stack of pallets. That is
 * the app's whole premise in one picture — the sticker on the thing is what a
 * worker scans to file a report about it.
 *
 * ## Why the subject changes
 *
 * It used to be the cable drum alone, which was right while this was Livprom's
 * internal tool and their drums were the product. It is a product for several
 * companies now, and a single customer's goods on the sign-in screen says the
 * wrong thing about who the software is for. Three subjects say "anything on
 * your floor" without naming anybody.
 *
 * ## Inline SVG, not an image
 *
 * This is the first screen anyone sees, often on plant wifi. Inline it costs no
 * request, stays sharp at any size, and cannot appear as a broken image if a
 * deploy misses the assets folder.
 *
 * All ids come from `useId()`. They are referenced by `url(#…)`, ids are
 * document-global, and a second copy of this SVG on the page would otherwise
 * make the first resolve its gradients and clips against the wrong element — a
 * bug this project has already had once, with the logo mark.
 */
export function LoginIllustration({ className }: { className?: string }) {
  const t = useT()
  const uid = useId().replace(/:/g, '')

  const wood = `${uid}-wood`
  const woodDark = `${uid}-wood-dark`
  const steel = `${uid}-steel`
  const tagClip = `${uid}-tag`
  const sweepGradient = `${uid}-sweep`
  const flangeClip = `${uid}-flange`

  /** One subject's turn on screen, in seconds. Three of them make the loop. */
  const BEAT = 6
  const LOOP = BEAT * 3

  return (
    <svg
      viewBox="0 0 440 420"
      className={cn('h-full w-full', className)}
      role="img"
      aria-label={t('brand.illustrationAlt')}
    >
      {/*
        Motion lives here rather than in index.css because it is inseparable
        from this drawing — the delays are choreography against specific shapes,
        and they would rot the moment either file moved without the other.

        Every rule sits inside `prefers-reduced-motion: no-preference`. The base
        SVG is already a finished picture, so a reader who has asked for less
        motion gets the first subject, held still, and nothing moves. Written the
        other way round — animating by default, disabling on request — an
        entrance that starts at `opacity: 0` leaves them looking at an empty
        panel.
      */}
      <style>{`
        /*
          Base state, and the reduced-motion state: the first subject is shown
          and the rest are hidden. Both come from an inline opacity per group
          rather than from a selector.

          It was :first-of-type and :nth-of-type(n), which count among sibling
          elements of the same tag — and the halo is a g too, so every subject
          was off by one. The delays came out 6s, 12s, 0s, and the reduced-motion
          rule matched the halo instead of a subject, leaving anyone who asks for
          less motion looking at an empty stage.

          (No backticks in this comment on purpose: the whole block is a JS
          template literal, and one would end it.)
        */
        @media (prefers-reduced-motion: no-preference) {
          .${uid}-halo {
            animation: ${uid}-halo 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards,
                       ${uid}-breathe 7s ease-in-out 700ms infinite;
          }

          /*
            Each subject holds for one beat and cross-fades out.

            The keyframes are written as percentages of the whole loop rather
            than of a beat, and each subject gets the same animation with a
            different delay. One timeline, three offsets — the alternative is
            three hand-tuned keyframe sets that drift apart the first time the
            beat changes.
          */
          .${uid}-subject {
            animation: ${uid}-cycle ${LOOP}s ease-in-out infinite;
            /* The animation owns opacity from here; the inline value below is
               only the still frame for reduced motion. */
            animation-fill-mode: both;
          }

          /* Only the drum turns; the others are not round. */
          .${uid}-spin {
            animation: ${uid}-spin 26s linear infinite;
            transform-box: view-box;
            transform-origin: 196px 212px;
          }

          /* The scan re-runs once per subject, landing just after it settles. */
          .${uid}-sweep { animation: ${uid}-sweep ${BEAT}s ease-in-out infinite; }
          .${uid}-tick  { animation: ${uid}-tick ${BEAT}s ease-out infinite; }

          .${uid}-tag {
            animation: ${uid}-drop 800ms cubic-bezier(0.34, 1.4, 0.64, 1) 300ms backwards;
          }
        }

        @keyframes ${uid}-cycle {
          0%                        { opacity: 0; transform: translateY(14px) scale(0.97); }
          4%, ${(100 / 3).toFixed(1)}% { opacity: 1; transform: translateY(0)    scale(1); }
          ${(100 / 3 + 4).toFixed(1)}%, 100% { opacity: 0; transform: translateY(-10px) scale(0.98); }
        }

        @keyframes ${uid}-halo {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }

        @keyframes ${uid}-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }

        @keyframes ${uid}-spin {
          to { transform: rotate(360deg); }
        }

        @keyframes ${uid}-drop {
          from { opacity: 0; transform: translateY(-26px) rotate(-8deg); }
          to   { opacity: 1; transform: translateY(0) rotate(0deg); }
        }

        /* Down the sticker, pause, and gone — not a metronome. */
        @keyframes ${uid}-sweep {
          0%        { transform: translateY(-58px); opacity: 0; }
          12%       { opacity: 1; }
          46%       { transform: translateY(58px); opacity: 1; }
          54%, 100% { transform: translateY(58px); opacity: 0; }
        }

        @keyframes ${uid}-tick {
          0%, 46%   { opacity: 0; transform: scale(0.6); }
          58%       { opacity: 1; transform: scale(1); }
          88%       { opacity: 1; transform: scale(1); }
          100%      { opacity: 0; transform: scale(1); }
        }
      `}</style>

      <defs>
        <linearGradient id={wood} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e8b57c" />
          <stop offset="1" stopColor="#c98a4b" />
        </linearGradient>
        <linearGradient id={woodDark} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a06a33" />
          <stop offset="1" stopColor="#7d4f24" />
        </linearGradient>
        <linearGradient id={steel} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9aa6b4" />
          <stop offset="1" stopColor="#5f6b7a" />
        </linearGradient>

        {/* Keeps the scan line inside the sticker instead of running off it. */}
        <clipPath id={tagClip}>
          <rect x="266" y="66" width="112" height="124" rx="14" />
        </clipPath>

        <linearGradient id={sweepGradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#34d399" stopOpacity="0" />
          <stop offset="0.5" stopColor="#34d399" stopOpacity="0.6" />
          <stop offset="1" stopColor="#34d399" stopOpacity="0" />
        </linearGradient>

        <clipPath id={flangeClip}>
          <circle cx="196" cy="212" r="119" />
        </clipPath>
      </defs>

      {/* Soft halo, so the subject does not sit flat on the panel colour. */}
      <g
        className={`${uid}-halo`}
        style={{ transformBox: 'view-box', transformOrigin: '205px 215px' }}
      >
        <circle cx="205" cy="215" r="168" fill="#fff" opacity="0.07" />
        <circle cx="205" cy="215" r="132" fill="#fff" opacity="0.05" />
      </g>

      {/* Contact shadow. Shared by all three, so it never blinks between them. */}
      <ellipse cx="205" cy="366" rx="138" ry="15" fill="#000" opacity="0.22" />

      {/*
        The three subjects, stacked in the same space.

        `:nth-of-type` drives the timing, so these must stay direct siblings and
        stay in this order — inserting anything between them shifts the whole
        rotation by one beat.
      */}
      <g
        className={`${uid}-subject`}
        style={{
          transformBox: 'view-box',
          transformOrigin: '205px 240px',
          animationDelay: '0s',
          opacity: 1,
        }}
      >
        {/* ── 1 · Wooden cable drum ─────────────────────────────────────── */}
        <circle cx="232" cy="200" r="112" fill={`url(#${woodDark})`} />
        <path d="M232 118 h14 a16 16 0 0 1 0 164 h-14 z" fill="#8a5a2b" />
        <circle cx="196" cy="212" r="122" fill={`url(#${wood})`} />
        <circle cx="196" cy="212" r="122" fill="none" stroke="#a86d35" strokeWidth="7" />
        <circle cx="196" cy="212" r="97" fill="none" stroke="#b9793d" strokeWidth="3" opacity="0.7" />

        {/*
          Radial planks carry the rotation — the flange is a plain circle, so
          turning it would be invisible. The six bolts break the twelve-fold
          symmetry, without which the spin reads as almost static.
        */}
        <g className={`${uid}-spin`}>
          <g stroke="#a86d35" strokeWidth="2.5" opacity="0.55" clipPath={`url(#${flangeClip})`}>
            {Array.from({ length: 12 }, (_, index) => {
              const angle = (index * Math.PI * 2) / 12
              return (
                <line
                  key={index}
                  x1={196 + Math.cos(angle) * 30}
                  y1={212 + Math.sin(angle) * 30}
                  x2={196 + Math.cos(angle) * 122}
                  y2={212 + Math.sin(angle) * 122}
                />
              )
            })}
          </g>
          <g fill="#8a5a2b" opacity="0.75">
            {Array.from({ length: 6 }, (_, index) => {
              const angle = (index * Math.PI * 2) / 6 + Math.PI / 12
              return (
                <circle
                  key={index}
                  cx={196 + Math.cos(angle) * 108}
                  cy={212 + Math.sin(angle) * 108}
                  r="4.5"
                />
              )
            })}
          </g>
        </g>

        <circle cx="196" cy="212" r="26" fill="#8a5a2b" />
        <circle cx="196" cy="212" r="12" fill="#6d4520" />
      </g>

      <g
        className={`${uid}-subject`}
        style={{
          transformBox: 'view-box',
          transformOrigin: '205px 240px',
          animationDelay: `${BEAT}s`,
          opacity: 0,
        }}
      >
        {/* ── 2 · A machine on the floor ────────────────────────────────── */}

        {/* Legs, drawn first so the body sits over them. */}
        <rect x="104" y="300" width="18" height="58" rx="4" fill="#4a5563" />
        <rect x="278" y="300" width="18" height="58" rx="4" fill="#4a5563" />

        {/* Body */}
        <rect x="88" y="196" width="224" height="118" rx="14" fill={`url(#${steel})`} />
        <rect x="88" y="196" width="224" height="118" rx="14" fill="none" stroke="#48525f" strokeWidth="4" />

        {/* Infeed table, with a board part-way through it. */}
        <rect x="52" y="228" width="52" height="12" rx="3" fill="#6f7b8a" />
        <rect x="36" y="222" width="60" height="10" rx="3" fill={`url(#${wood})`} />

        {/* Dust hood */}
        <path d="M232 196 l26 -44 h34 l-16 44 z" fill="#6f7b8a" />
        <rect x="256" y="140" width="42" height="16" rx="6" fill="#48525f" />

        {/* Cutter block window, and the blades inside it. */}
        <rect x="120" y="216" width="86" height="52" rx="8" fill="#2f3742" />
        <g stroke="#8b98a8" strokeWidth="3" opacity="0.8">
          <line x1="132" y1="224" x2="132" y2="260" />
          <line x1="150" y1="224" x2="150" y2="260" />
          <line x1="168" y1="224" x2="168" y2="260" />
          <line x1="186" y1="224" x2="186" y2="260" />
        </g>

        {/* Control panel — the detail that makes it read as a machine and not a
            crate. Two lamps and a dial. */}
        <rect x="224" y="220" width="70" height="48" rx="8" fill="#39424e" />
        <circle cx="243" cy="238" r="7" fill="#34d399" />
        <circle cx="265" cy="238" r="7" fill="#f59e0b" opacity="0.85" />
        <rect x="236" y="252" width="46" height="7" rx="3.5" fill="#8b98a8" opacity="0.65" />

        {/* Base skirt */}
        <rect x="96" y="292" width="208" height="16" rx="6" fill="#48525f" />
      </g>

      <g
        className={`${uid}-subject`}
        style={{
          transformBox: 'view-box',
          transformOrigin: '205px 240px',
          animationDelay: `${BEAT * 2}s`,
          opacity: 0,
        }}
      >
        {/* ── 3 · A stack of pallets ────────────────────────────────────── */}
        {Array.from({ length: 4 }, (_, index) => {
          const y = 300 - index * 46
          const shade = index % 2 === 0 ? wood : woodDark

          return (
            <g key={index}>
              {/* Deck boards */}
              <rect x="96" y={y} width="220" height="13" rx="3" fill={`url(#${shade})`} />
              <rect x="96" y={y + 20} width="220" height="13" rx="3" fill={`url(#${shade})`} />

              {/* Blocks between the decks — three, like a real stringer pallet. */}
              <rect x="104" y={y + 13} width="26" height="20" fill="#8a5a2b" />
              <rect x="193" y={y + 13} width="26" height="20" fill="#8a5a2b" />
              <rect x="282" y={y + 13} width="26" height="20" fill="#8a5a2b" />
            </g>
          )
        })}
      </g>

      {/* ── The sticker, and the scan ──────────────────────────────────────
          Fixed in place while the subjects change beneath it: the sticker is
          the constant in this story, not the thing it is stuck to. */}
      <g
        className={`${uid}-tag`}
        style={{ transformBox: 'view-box', transformOrigin: '322px 128px' }}
      >
        <rect x="266" y="66" width="112" height="124" rx="14" fill="#fdfbf7" />
        <rect x="266" y="66" width="112" height="124" rx="14" fill="none" stroke="#e6dccd" strokeWidth="2" />

        {/* A QR-ish block. Deliberately not a scannable code — a real one on a
            login screen is an invitation to point a phone at nothing. */}
        <g fill="#1f2937">
          <rect x="282" y="82" width="26" height="26" rx="4" />
          <rect x="336" y="82" width="26" height="26" rx="4" />
          <rect x="282" y="136" width="26" height="26" rx="4" />
          <g opacity="0.9">
            <rect x="316" y="86" width="8" height="8" />
            <rect x="316" y="102" width="8" height="8" />
            <rect x="320" y="120" width="8" height="8" />
            <rect x="336" y="120" width="8" height="8" />
            <rect x="352" y="120" width="8" height="8" />
            <rect x="316" y="140" width="8" height="8" />
            <rect x="332" y="148" width="8" height="8" />
            <rect x="348" y="136" width="8" height="8" />
            <rect x="352" y="156" width="8" height="8" />
            <rect x="320" y="164" width="8" height="8" />
          </g>
        </g>

        <rect x="282" y="172" width="80" height="6" rx="3" fill="#cbd5e1" />

        {/* The sweep, clipped to the sticker. */}
        <g clipPath={`url(#${tagClip})`}>
          <rect
            className={`${uid}-sweep`}
            x="266"
            y="118"
            width="112"
            height="22"
            fill={`url(#${sweepGradient})`}
          />
        </g>
      </g>

      {/* Confirmation, once per scan. */}
      <g
        className={`${uid}-tick`}
        style={{ transformBox: 'view-box', transformOrigin: '366px 178px' }}
      >
        <circle cx="366" cy="178" r="21" fill="#34d399" />
        <path
          d="M356 178 l7 7 l14 -15"
          fill="none"
          stroke="#0b3a2a"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  )
}
